/**
 * The add-in's naming pass for the proposal engine: the one place the document
 * open in Word is given paragraph ids under a naming scheme, so the reader
 * route (`document-paragraphs`) sees a name on every body paragraph the core
 * may address.
 *
 * The pass is the same rule the web applies to a stored file in
 * `OoxmlRange.nameUnnamedParagraphs`, written against Office.js instead of
 * OOXML: strip every name of a scheme the active naming retires, then give
 * every body paragraph that owns no name under the active scheme one fresh
 * name, checked against every bookmark already in the document. Table cell
 * paragraphs are body paragraphs to Office.js (`body.paragraphs` lists them),
 * so a row Word inserts is named on the next pass like any other paragraph.
 *
 * Callers wrap this in `withWordContextWrite`. Its one caller is
 * `nameEveryParagraphUnderTheActiveScheme` in `paragraph-bookmarks.ts`, which
 * hands over to it when the org's proposal-engine flag is on; with the flag off
 * that module's own `_dioptra_p_` pass runs instead.
 */
import { RETIRED_PARAGRAPH_BOOKMARK_PREFIXES, freshParagraphBookmarkName, isParagraphBookmarkName } from "@common/utils/redline/ooxml/paragraph-bookmark-naming";
import { WordTokenizer } from "@common/utils/redline/text/word-tokenizer";

import { readParagraphIdOwningEachBookmark } from "./paragraph-bookmark-ownership";

/** What one pass did to the open document: retired names deleted, the names minted, and the names laid back where they had been, in paragraph order. */
export interface EngineNamingPassResult {
    removedLegacyNames: string[];
    addedNames: string[];
    restoredNames: string[];
}

/** The paragraph a name last lay on, and how that paragraph read then. */
interface RememberedParagraphName {
    bookmarkName: string;
    uniqueLocalId: string;
    collapsedText: string;
}

/**
 * The names every pass of this taskpane session has seen on the document's
 * paragraphs, so a name the user's Undo took off a paragraph goes back onto
 * that paragraph instead of a fresh name taking its place.
 *
 * WHY
 *   A paragraph's name is its id to the agent and to every card proposed on it.
 *   Word's Undo removes the add-in's bookmarks like any other edit; minting fresh
 *   names then made every id the agent held stale and left every card looking
 *   for a bookmark that would never come back.
 *
 * CASES, for a paragraph that owns no name
 *   - its `uniqueLocalId` last carried a name that no paragraph carries now -> that name, whatever the paragraph reads
 *   - its id is new, and exactly one free remembered name was last seen on a
 *     paragraph reading word for word as this one                           -> that name
 *   - otherwise                                                              -> none: a fresh name is minted
 */
export class ParagraphNameMemory {
    private readonly rememberedByParagraphId = new Map<string, RememberedParagraphName>();

    remember(paragraph: Word.Paragraph, bookmarkName: string): void {
        this.rememberedByParagraphId.set(paragraph.uniqueLocalId, { bookmarkName, uniqueLocalId: paragraph.uniqueLocalId, collapsedText: WordTokenizer.collapse(paragraph.text) });
    }

    nameToLayBack(paragraph: Word.Paragraph, bookmarkNamesInUse: Set<string>): string | null {
        const lastCarried = this.rememberedByParagraphId.get(paragraph.uniqueLocalId);
        if (lastCarried !== undefined && !bookmarkNamesInUse.has(lastCarried.bookmarkName)) return lastCarried.bookmarkName;
        const collapsedText = WordTokenizer.collapse(paragraph.text);
        /**
         * WHEN the paragraph reads as nothing (an empty paragraph)
         *   CONSEQUENCE
         *     no name is matched by text: every empty paragraph reads alike.
         */
        if (collapsedText.length === 0) return null;
        /**
         * One name may be remembered under several ids (the paragraph's id changed and the name
         * was laid back on it); it counts once.
         */
        const freeNamesLastSeenOnThisText = new Set(
            Array.from(this.rememberedByParagraphId.values())
                .filter((remembered) => remembered.collapsedText === collapsedText && !bookmarkNamesInUse.has(remembered.bookmarkName))
                .map((remembered) => remembered.bookmarkName),
        );
        return freeNamesLastSeenOnThisText.size === 1 ? Array.from(freeNamesLastSeenOnThisText)[0] : null;
    }
}

/**
 * Names every body paragraph of the open document under `naming`.
 *
 * SYNC ROUNDS, in order
 *   1. read every bookmark name in the body and load the paragraphs
 *   2. delete the retired names (skipped when there are none)
 *   3. find the first paragraph of each paragraph name in use  (`readIdsOfParagraphsOwningABookmark`)
 *   4. compare each name's range with that paragraph           (`readIdsOfParagraphsOwningABookmark`)
 *   5. insert the fresh names
 *
 * CASES, per paragraph
 *   - owns a name under `naming` that lies on it   -> untouched
 *   - owns none, or only names that drifted away   -> one fresh name inserted on its content range
 *
 * RETURNS
 *   The retired names deleted and the names minted; both empty when the
 *   document was already named under `naming`, so a second pass is a no-op.
 */
export async function nameParagraphsForEngine(context: Word.RequestContext, paragraphNameMemory: ParagraphNameMemory): Promise<EngineNamingPassResult> {
    const documentBookmarkNames = context.document.body.getRange().getBookmarks(true, false);
    const bodyParagraphs = context.document.body.paragraphs;
    bodyParagraphs.load("uniqueLocalId,text");
    await context.sync();

    const removedLegacyNames = documentBookmarkNames.value.filter((bookmarkName) => RETIRED_PARAGRAPH_BOOKMARK_PREFIXES.some((retiredPrefix) => bookmarkName.startsWith(retiredPrefix)));
    const removedLegacyNameSet = new Set(removedLegacyNames);
    const bookmarkNamesInUse = new Set(documentBookmarkNames.value.filter((bookmarkName) => !removedLegacyNameSet.has(bookmarkName)));
    /**
     * WHEN the document carries names of a retired scheme
     *   CONSEQUENCE
     *     delete them all before any paragraph is examined, so no paragraph is judged named by a retired name.
     */
    if (removedLegacyNames.length > 0) {
        for (const retiredName of removedLegacyNames) context.document.deleteBookmark(retiredName);
        await context.sync();
    }

    const paragraphNamesInUse = [...bookmarkNamesInUse].filter(isParagraphBookmarkName);
    const paragraphIdOwningEachName = await readParagraphIdOwningEachBookmark(context, paragraphNamesInUse);
    const nameOwnedByParagraphId = new Map(Array.from(paragraphIdOwningEachName, ([bookmarkName, paragraphId]) => [paragraphId, bookmarkName]));

    const addedNames: string[] = [];
    const restoredNames: string[] = [];
    bodyParagraphs.items.forEach((paragraph) => {
        /**
         * WHEN a name under `naming` already lies on this paragraph
         *   CONSEQUENCE
         *     the paragraph keeps it and no fresh name is minted, so a second pass is a no-op.
         */
        const ownedName = nameOwnedByParagraphId.get(paragraph.uniqueLocalId);
        if (ownedName !== undefined) {
            paragraphNameMemory.remember(paragraph, ownedName);
            return;
        }
        /**
         * CASES
         *   - a name this paragraph carried before is free -> laid back, so the agent's id and every card's bookmark hold
         *   - otherwise                                     -> a fresh name
         */
        const nameLaidBack = paragraphNameMemory.nameToLayBack(paragraph, bookmarkNamesInUse);
        const bookmarkName = nameLaidBack ?? freshParagraphBookmarkName(bookmarkNamesInUse);
        bookmarkNamesInUse.add(bookmarkName);
        paragraph.getRange(Word.RangeLocation.content).insertBookmark(bookmarkName);
        (nameLaidBack === null ? addedNames : restoredNames).push(bookmarkName);
        paragraphNameMemory.remember(paragraph, bookmarkName);
    });
    await context.sync();
    return { removedLegacyNames, addedNames, restoredNames };
}
